import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";

import "./FaceScanner.css";

type FaceScannerProps = {
  isOpen: boolean;
  onClose: () => void;
  onCaptured: (imageDataUrl: string) => void;
};

const MODEL_BASE_URL = `${process.env.PUBLIC_URL || ""}/models`;

function getFaceApi() {
  return (window as any).faceapi as any;
}

async function assertModelManifestsAvailable(modelBaseUrl: string) {
  const requiredManifests = [
    `${modelBaseUrl}/tiny_face_detector_model-weights_manifest.json`,
    `${modelBaseUrl}/face_landmark_68_model-weights_manifest.json`,
  ];

  for (const url of requiredManifests) {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) {
      throw new Error(
        `Model files are missing. Expected ${url}.\nCopy the face-api model files into public/models.\nSee public/models/README.txt.`
      );
    }

    const text = (await res.text()).trim();
    if (text.startsWith("<!DOCTYPE") || text.startsWith("<html")) {
      throw new Error(
        `Model files are missing. '${url}' returned HTML instead of JSON.\nCopy the face-api model files into public/models.\nSee public/models/README.txt.`
      );
    }

    try {
      JSON.parse(text);
    } catch {
      throw new Error(
        `Model manifest is not valid JSON at ${url}.\nRe-copy the model files into public/models.`
      );
    }
  }
}

const FaceScanner: React.FC<FaceScannerProps> = ({
  isOpen,
  onClose,
  onCaptured,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const intervalRef = useRef<number | null>(null);

  const [faceDetected, setFaceDetected] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [modelWarning, setModelWarning] = useState<string>("");
  const [isReady, setIsReady] = useState<boolean>(false);
  const [modelsLoaded, setModelsLoaded] = useState<boolean>(false);
  const [stageAspectRatio, setStageAspectRatio] = useState<string>("");

  const stopCamera = () => {
    const video = videoRef.current;
    const stream = video?.srcObject as MediaStream | null;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
    }
    if (video) {
      video.srcObject = null;
    }
  };

  const clearDetectionLoop = () => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const startCamera = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });

    if (!videoRef.current) return;
    videoRef.current.srcObject = stream;

    await new Promise<void>((resolve) => {
      const v = videoRef.current;
      if (!v) return resolve();
      const handler = () => {
        v.removeEventListener("loadedmetadata", handler);
        const w = v.videoWidth;
        const h = v.videoHeight;
        if (w > 0 && h > 0) {
          setStageAspectRatio(`${w} / ${h}`);
        }
        resolve();
      };
      v.addEventListener("loadedmetadata", handler);
    });

    await videoRef.current.play();
  };

  const loadModels = async () => {
    const faceapi = getFaceApi();
    if (!faceapi) {
      throw new Error(
        "Face detection library not loaded. Expected %PUBLIC_URL%/vendor/face-api.min.js to be available."
      );
    }
    // await assertModelManifestsAvailable(MODEL_BASE_URL);
    await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_BASE_URL);
    await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_BASE_URL);
  };

  const detectFace = () => {
    clearDetectionLoop();
    intervalRef.current = window.setInterval(async () => {
      const faceapi = getFaceApi();
      if (!faceapi) return;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) return;

      const width = video.videoWidth;
      const height = video.videoHeight;
      if (!width || !height) return;

      const detections = await faceapi
        .detectAllFaces(video, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks();

      const displaySize = { width, height };
      canvas.width = width;
      canvas.height = height;

      faceapi.matchDimensions(canvas, displaySize);
      const resized = faceapi.resizeResults(detections, displaySize);

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw only the outer bounding box 
      ctx.strokeStyle = "#0d6efd"; // Bootstrap primary
      ctx.lineWidth = 3;
      for (const det of resized) {
        const { x, y, width: w, height: h } = det.detection.box;
        ctx.strokeRect(x, y, w, h);
      }

      setFaceDetected(detections.length > 0);
    }, 500);
  };

  const [captureValidation, setCaptureValidation] = useState<string>("");

  const captureImage = async () => {
    setCaptureValidation("");

    const video = videoRef.current;
    if (!video) return;
    if (!video.videoWidth || !video.videoHeight) return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);

    // If models aren't loaded, we can't validate; keep existing behavior
    if (!modelsLoaded) {
      const image = canvas.toDataURL("image/png");
      onCaptured(image);
      onClose();
      return;
    }

    try {
      const faceapi = getFaceApi();
      if (!faceapi) {
        const image = canvas.toDataURL("image/png");
        onCaptured(image);
        onClose();
        return;
      }

      const detections = await faceapi
        .detectAllFaces(canvas, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks();

      if (detections.length > 1) {
        setCaptureValidation("Multiple faces detected. Please capture only one person.");
        return;
      }

      if (detections.length === 0) {
        setCaptureValidation("No face detected. Please retake the photo.");
        return;
      }

      const image = canvas.toDataURL("image/png");
      onCaptured(image);
      onClose();
    } catch {
      // If validation fails unexpectedly, don’t block capture
      const image = canvas.toDataURL("image/png");
      onCaptured(image);
      onClose();
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setCaptureValidation("");

    let cancelled = false;
    setError("");
    setModelWarning("");
    setIsReady(false);
    setFaceDetected(false);
    setModelsLoaded(false);

    (async () => {
      try {
        await startCamera();
        if (cancelled) return;
        setIsReady(true);

        try {
          await loadModels();
          if (cancelled) return;
          setModelsLoaded(true);
          detectFace();
        } catch (e) {
          const message =
            e instanceof Error
              ? e.message
              : "Face detection models could not be loaded. You can still capture a photo.";
          setModelWarning(message);
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : "Unable to start camera.";
        setError(message);
      }
    })();

    return () => {
      cancelled = true;
      clearDetectionLoop();
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    setCaptureValidation("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg" backdrop="static">
      <ModalHeader toggle={handleClose}>Face Scanner</ModalHeader>
      <ModalBody>
        {error ? (
          <div className="text-danger" style={{ whiteSpace: "pre-wrap" }}>
            {error}
          </div>
        ) : (
          <div className="fs-scanner">
            <div className="fs-stage" style={stageAspectRatio ? { aspectRatio: stageAspectRatio } : undefined}>
              <video ref={videoRef} autoPlay muted className="fs-video" />
              <canvas ref={canvasRef} className="fs-canvas" />
            </div>

            <div className="d-flex align-items-center justify-content-between mt-3">
              <div className="text-secondary">
                {!isReady
                  ? "Starting camera..."
                  : modelsLoaded
                    ? faceDetected
                      ? "Face detected"
                      : "No face detected"
                    : "Face detection not loaded"}
              </div>

              <Button color="primary" onClick={captureImage} disabled={!isReady}>
                Capture Face
              </Button>
            </div>

            {captureValidation ? (
              <div className="text-danger mt-2">{captureValidation}</div>
            ) : null}

            {modelWarning ? (
              <div className="text-warning mt-3" style={{ whiteSpace: "pre-wrap" }}>
                {modelWarning}
              </div>
            ) : null}
          </div>

        )}
      </ModalBody>
    </Modal>
  );
};

export default FaceScanner;
